import React, { useState, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X, ChevronDown, Search, HeartPulse } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from './button';

// ============================================================================
// Medical condition categories & items
// ============================================================================

interface ConditionCategory {
  label: string;
  items: string[];
}

const CONDITION_CATEGORIES: ConditionCategory[] = [
  {
    label: 'Cardiovascular',
    items: [
      'Heart Disease',
      'Heart Attack (MI)',
      'High Blood Pressure',
      'High Cholesterol',
      'Blood Clots',
      'Congestive Heart Failure',
      'Stroke',
    ],
  },
  {
    label: 'Endocrine',
    items: [
      'Diabetes (Type 1)',
      'Diabetes (Type 2)',
      'Thyroid Disease',
      'PCOS',
    ],
  },
  {
    label: 'Mental Health',
    items: [
      'Anxiety',
      'Depression',
      'Bipolar Disorder',
      'Schizophrenia',
      'PTSD',
    ],
  },
  {
    label: 'Respiratory',
    items: [
      'Asthma',
      'COPD / Emphysema',
      'Sleep Apnea',
    ],
  },
  {
    label: 'Gastrointestinal',
    items: [
      'Acid Reflux / GERD',
      'Colitis / Crohns',
      'Irritable Bowel Syndrome',
      'Liver Disease',
      'Gallstones',
    ],
  },
  {
    label: 'Musculoskeletal',
    items: [
      'Arthritis',
      'Osteoporosis',
      'Gout',
    ],
  },
  {
    label: 'Renal',
    items: [
      'Kidney Disease',
      'Kidney Stones',
    ],
  },
  {
    label: 'Other',
    items: [
      'Addiction / Substance Abuse',
      'Anemia',
      'Autoimmune Disease',
      'Cancer',
      'Dementia',
      'Epilepsy / Seizures',
      'Glaucoma',
      'Hearing Impairment',
      'HIV',
      'Migraines',
      'Osteoporosis',
      'Parkinson\'s Disease',
      'Skin Disease',
      'Tuberculosis',
      'Vision Impairment',
    ],
  },
];

const ALL_CONDITIONS = CONDITION_CATEGORIES.flatMap(c => c.items);

// ============================================================================
// JSON serialization helpers
// ============================================================================

export interface MedicalHistoryValue {
  conditions: string[];
  other: string;
}

/**
 * Parse the raw `medical_history` TEXT column value.
 *  – Valid JSON with `conditions` array → use as-is
 *  – Plain string (legacy freeform)   → put in `other`
 *  – Empty / null                     → empty state
 */
export function parseMedicalHistory(raw: string | null | undefined): MedicalHistoryValue {
  if (!raw || !raw.trim()) {
    return { conditions: [], other: '' };
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.conditions)) {
      return {
        conditions: parsed.conditions.filter((c: unknown) => typeof c === 'string'),
        other: typeof parsed.other === 'string' ? parsed.other : '',
      };
    }
  } catch {
    // Not valid JSON — treat as legacy freeform text
  }

  return { conditions: [], other: raw };
}

/**
 * Serialize conditions + other text back to a JSON string for storage.
 */
export function serializeMedicalHistory(value: MedicalHistoryValue): string {
  if (value.conditions.length === 0 && !value.other.trim()) {
    return '';
  }
  return JSON.stringify({ conditions: value.conditions, other: value.other });
}

// ============================================================================
// Component
// ============================================================================

interface MedicalHistoryMultiSelectProps {
  form?: UseFormReturn<any>;
  name?: string;
  value?: MedicalHistoryValue;
  onChange?: (next: MedicalHistoryValue) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  dark?: boolean;
}

const MedicalHistoryMultiSelect: React.FC<MedicalHistoryMultiSelectProps> = ({
  form,
  name,
  value,
  onChange,
  label,
  placeholder = 'Select medical conditions...',
  required = false,
  disabled = false,
  className,
  dark = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const isControlled = onChange !== undefined;

  // In controlled mode, read directly from `value`; otherwise parse the form field.
  const rawValue: string = isControlled ? '' : (form && name ? form.watch(name) : '') || '';
  const parsed: MedicalHistoryValue = useMemo(
    () => (isControlled ? (value ?? { conditions: [], other: '' }) : parseMedicalHistory(rawValue)),
    [isControlled, value, rawValue]
  );

  // ------ helpers ------

  const updateForm = (next: MedicalHistoryValue) => {
    if (isControlled) {
      onChange!(next);
    } else if (form && name) {
      form.setValue(name, serializeMedicalHistory(next), { shouldValidate: true, shouldDirty: true });
    }
  };

  const toggleCondition = (condition: string) => {
    const next = parsed.conditions.includes(condition)
      ? parsed.conditions.filter(c => c !== condition)
      : [...parsed.conditions, condition];
    updateForm({ ...parsed, conditions: next });
  };

  const removeCondition = (condition: string) => {
    updateForm({ ...parsed, conditions: parsed.conditions.filter(c => c !== condition) });
  };

  const handleOtherChange = (text: string) => {
    updateForm({ ...parsed, other: text });
  };

  // ------ search filtering ------

  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return CONDITION_CATEGORIES;
    const q = searchTerm.toLowerCase();
    return CONDITION_CATEGORIES
      .map(cat => ({
        ...cat,
        items: cat.items.filter(item => item.toLowerCase().includes(q)),
      }))
      .filter(cat => cat.items.length > 0);
  }, [searchTerm]);

  // ------ render ------

  return (
    <div className={cn('space-y-3', className)}>
      {label && (
        <Label className={required ? 'required' : ''}>
          {label} {required && <span className='text-red-500'>*</span>}
        </Label>
      )}

      {/* Trigger Button */}
      <button
        type='button'
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{ minHeight: '40px' }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm',
          'transition-colors',
          'focus:outline-none focus:ring-0',
          'disabled:cursor-not-allowed disabled:opacity-50',
          dark
            ? 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.08)]'
            : 'bg-white border-slate-200 hover:bg-slate-50'
        )}
      >
        <div className='flex min-h-[24px] flex-1 flex-wrap items-center gap-1.5'>
          {parsed.conditions.length === 0 ? (
            <span className={cn('text-sm', dark ? 'text-white/40' : 'text-slate-500')}>{placeholder}</span>
          ) : (
            parsed.conditions.map((condition) => (
              <Badge
                key={condition}
                className='flex items-center gap-1 bg-brand-teal py-1 pl-2.5 pr-1 text-xs font-medium text-white hover:bg-brand-teal'
              >
                <span className='max-w-[180px] truncate'>{condition}</span>
                <button
                  type='button'
                  onClick={e => {
                    e.stopPropagation();
                    removeCondition(condition);
                  }}
                  style={{ minHeight: 'auto' }}
                  className='ml-0.5 rounded-full p-0.5 transition-colors hover:bg-white/20'
                  aria-label={`Remove ${condition}`}
                >
                  <X className='h-3 w-3' />
                </button>
              </Badge>
            ))
          )}
        </div>
        <ChevronDown className={cn('h-4 w-4 shrink-0', dark ? 'text-white/40' : 'text-slate-400')} />
      </button>

      {/* Dialog for Selection */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='gap-0 overflow-hidden p-0 sm:max-w-[520px]'>
          {/* Header */}
          <div className='border-b border-slate-100 px-5 pb-4 pt-5'>
            <div className='flex items-center gap-3'>
              <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-brand-teal/10'>
                <HeartPulse className='h-5 w-5 text-brand-teal' />
              </div>
              <div>
                <DialogTitle className='text-base font-semibold text-slate-900'>
                  Medical History
                </DialogTitle>
                <DialogDescription className='mt-0.5 text-xs text-slate-500'>
                  Select all conditions that apply
                </DialogDescription>
              </div>
            </div>
          </div>

          {/* Search Input */}
          <div className='border-b border-slate-100 bg-slate-50/50 px-5 py-3'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
              <Input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder='Search conditions...'
                className='h-10 border-slate-200 bg-white pl-9 text-sm placeholder:text-slate-400 focus-visible:border-brand-teal focus-visible:ring-1 focus-visible:ring-brand-teal'
                autoFocus
              />
            </div>
          </div>

          {/* Categorized Condition List */}
          <div className='overflow-y-auto' style={{ maxHeight: '400px' }}>
            {filteredCategories.length === 0 ? (
              <div className='flex flex-col items-center justify-center px-4 py-10'>
                <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100'>
                  <Search className='h-6 w-6 text-slate-300' />
                </div>
                <p className='text-sm font-medium text-slate-700'>No conditions found</p>
                <p className='mt-1 text-xs text-slate-500'>No results for "{searchTerm}"</p>
              </div>
            ) : (
              <div className='py-1'>
                {filteredCategories.map(category => (
                  <div key={category.label}>
                    {/* Category Header */}
                    <div className='sticky top-0 z-10 bg-slate-50 px-5 py-2'>
                      <span className='text-xs font-semibold uppercase tracking-wider text-slate-400'>
                        {category.label}
                      </span>
                    </div>
                    {/* Condition Items */}
                    {category.items.map(condition => {
                      const isSelected = parsed.conditions.includes(condition);
                      return (
                        <button
                          key={condition}
                          type='button'
                          className={cn(
                            'flex w-full items-center gap-3 px-5 py-2.5 text-left transition-colors',
                            isSelected
                              ? 'border-l-2 border-brand-teal bg-brand-teal/5'
                              : 'border-l-2 border-transparent hover:bg-slate-50'
                          )}
                          onClick={() => toggleCondition(condition)}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleCondition(condition)}
                            className={cn(
                              'border-slate-300',
                              isSelected &&
                                'border-brand-teal bg-brand-teal data-[state=checked]:border-brand-teal data-[state=checked]:bg-brand-teal'
                            )}
                          />
                          <span
                            className={cn(
                              'flex-1 text-sm',
                              isSelected ? 'font-medium text-brand-teal' : 'text-slate-700'
                            )}
                          >
                            {condition}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className='flex items-center justify-between border-t bg-slate-50/50 px-5 py-3'>
            {parsed.conditions.length > 0 && (
              <div>
                <p className='text-sm text-slate-600'>
                  <span className='font-medium text-brand-teal'>{parsed.conditions.length}</span>{' '}
                  condition{parsed.conditions.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            )}
            <Button type='button' onClick={() => setOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Other" textarea — always visible below trigger */}
      <div className='space-y-1.5'>
        <Label
          htmlFor={`${name || 'medical_history'}_other`}
          className='text-xs text-slate-500'
        >
          Other medical history not listed above
        </Label>
        <Textarea
          id={`${name || 'medical_history'}_other`}
          rows={3}
          value={parsed.other}
          onChange={e => handleOtherChange(e.target.value)}
          placeholder='Surgeries, hospitalizations, other conditions...'
          disabled={disabled}
        />
      </div>

      {!isControlled && form && name && form.formState.errors[name] && (
        <p className='text-[0.8rem] text-red-500'>
          {form.formState.errors[name]?.message as string}
        </p>
      )}
    </div>
  );
};

export default MedicalHistoryMultiSelect;
